import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { hrRequisitionApi, fetchCurrentEmployee } from "../api";
import type { HrRequisitionLine, RequisitionType, ProcurementItemType } from "../types";
import { assetApi } from "#/features/assets/api";
import { inventoryApi } from "#/features/inventory/api";
import { fetchDepartmentOptions } from "#/features/employees/api";
import { useAsyncData } from "#/features/inventory/hooks";
import { useAuth } from "#/context/AuthProvider";

type LineRow = Omit<HrRequisitionLine, "id" | "approvedQuantity" | "issuedQuantity" | "fulfillmentStatus">;

// Confirmed 2026-07-08 — GOODS/ASSET_REQUEST/SERVICE/WORKS/GENERAL are the only real,
// persisted backend values. There is no CONSUMABLE/STATIONERY/ASSET split at this level —
// "stationery vs consumable" doesn't exist; GOODS covers everything issued from the store.
//
// UPDATE 2026-07-09 — SERVICE/WORKS/GENERAL removed from this form by decision: Staff
// Requisitions is for items already in stock (Dept Head -> Store Officer approval,
// no budget lines). Anything needing external procurement (quotations/budget/MD approval)
// goes through the separate Procurement module instead, which already owns that full flow.
// The types/statuses themselves are left intact in types.ts for any historical records.
const TYPE_OPTS: { value: RequisitionType; label: string; hint: string }[] = [
  { value: "GOODS",         label: "Goods",          hint: "Items issued from the inventory store" },
  { value: "ASSET_REQUEST", label: "Asset Request",  hint: "Request an assignable asset (laptop, phone, vehicle key)" },
];

// Line-level itemType mapping — only GOODS and ASSET_REQUEST have a fixed classification;
// SERVICE/WORKS map straight across; GENERAL has no clean itemType equivalent so it's left unset.
function itemTypeForRequisitionType(type: RequisitionType): ProcurementItemType | undefined {
  switch (type) {
    case "GOODS": return "CONSUMABLE";
    case "ASSET_REQUEST": return "CAPITAL_ASSET";
    case "SERVICE": return "SERVICE";
    case "WORKS": return "WORKS";
    default: return undefined;
  }
}

function emptyLine(order: number, type: RequisitionType): LineRow {
  return {
    lineOrder: order,
    description: "",
    quantity: 1,
    itemType: itemTypeForRequisitionType(type) ?? null,
    inventoryItemId: null,
    assetCategoryId: null,
  };
}

export default function RequisitionCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasRole, hasAnyRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  // Same permission used elsewhere to identify the Procurement Officer acting on Staff
  // Requisitions (see RequisitionApprovalsPage's canProcessProcurement) — everyone else gets
  // their own department locked in, since departmentId is @NotNull with no server fallback.
  const canPickAnyDepartment = isAdmin || hasPermission("HR_REQUISITION_PROCUREMENT_PROCESS");

  const [type, setType]     = useState<RequisitionType>("GOODS");
  const [departmentId, setDepartmentId] = useState<number | "">("");
  const [purpose, setPurpose] = useState("");
  const [lines, setLines]   = useState<LineRow[]>([emptyLine(1, "GOODS")]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const { data: departments } = useAsyncData(() => fetchDepartmentOptions(), []);
  const { data: currentEmployee } = useAsyncData(() => fetchCurrentEmployee(), []);

  // Prefill (and, for non-Procurement-Officer users, lock) the department to the requester's
  // own — only once, so it doesn't stomp on a Procurement Officer's manual pick after the fact.
  useEffect(() => {
    if (departmentId === "" && currentEmployee?.primaryDepartmentId) {
      setDepartmentId(currentEmployee.primaryDepartmentId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmployee]);

  // Load inventory items (GOODS only — server-side filter) + asset categories for linked pickers
  const { data: inventoryItems } = useAsyncData(
    () => inventoryApi.listItems({ size: 200 }).then(r => r.content),
    [],
  );
  const { data: assetCategories } = useAsyncData(
    () => assetApi.listCategories(),
    [],
  );

  function changeType(t: RequisitionType) {
    setType(t);
    setLines([emptyLine(1, t)]);
  }

  function updateLine(i: number, field: keyof LineRow, value: unknown) {
    setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  }

  function addLine() {
    setLines((prev) => [...prev, emptyLine(prev.length + 1, type)]);
  }

  function removeLine(i: number) {
    setLines((prev) => prev.filter((_, idx) => idx !== i).map((l, idx) => ({ ...l, lineOrder: idx + 1 })));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!departmentId) {
      setError("Department is required.");
      return;
    }
    if (lines.some((l) => !l.description.trim())) {
      setError("All lines must have a description.");
      return;
    }
    // Backend now rejects (400) GOODS/CONSUMABLE lines with no linked inventoryItemId — a
    // free-text-only line can't be checked against stock later. Catch it here instead of
    // letting the create call fail after the user has filled in the whole form.
    if (isGoods && lines.some((l) => !l.inventoryItemId)) {
      setError("Every Goods line must be linked to a catalog inventory item — pick one from the dropdown.");
      return;
    }
    // Backend doesn't enforce this one yet (confirmed 2026-07-13 — same class of gap as the
    // CONSUMABLE/inventoryItemId one above, just not closed server-side), but enforce it
    // client-side anyway: an Asset Request line with no category is equally unfulfillable.
    if (isAsset && lines.some((l) => !l.assetCategoryId)) {
      setError("Every Asset Request line must be linked to an asset category — pick one from the dropdown.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const req = await hrRequisitionApi.create({ departmentId: departmentId as number, requisitionType: type, purpose: purpose || undefined, lines });
      navigate(`/hr/requisitions/${req.id}`);
    } catch (err: unknown) {
      setError(String((err as { message?: string })?.message ?? err));
      setSaving(false);
    }
  }

  const isGoods  = type === "GOODS";
  const isAsset  = type === "ASSET_REQUEST";
  const isPlain  = !isGoods && !isAsset;

  // GOODS type: pick from inventory catalog (name = inventoryItemId, description auto-fills)
  function pickInventoryItem(lineIdx: number, itemId: number) {
    const item = (inventoryItems ?? []).find(i => i.id === itemId);
    updateLine(lineIdx, "inventoryItemId", itemId);
    if (item) updateLine(lineIdx, "description", item.name);
  }

  // ASSET_REQUEST type: pick from assignable asset categories
  function pickAssetCategory(lineIdx: number, catId: number) {
    const cat = (assetCategories ?? []).find(c => c.id === catId);
    updateLine(lineIdx, "assetCategoryId", catId);
    if (cat) updateLine(lineIdx, "description", `${cat.name} (${cat.code})`);
  }

  const assignableCategories = (assetCategories ?? []).filter(c => c.assignable !== false);

  // Prefill line 1 from a catalog "Request" click (/hr/requisitions/new?itemId=123) — only
  // once the catalog has loaded and only while the form is still untouched, so it never
  // overwrites something the user has already picked.
  const prefillItemId = searchParams.get("itemId");
  useEffect(() => {
    if (!prefillItemId || !inventoryItems) return;
    if (type !== "GOODS") return;
    if (lines[0]?.inventoryItemId) return;
    const id = Number(prefillItemId);
    if (!Number.isFinite(id)) return;
    pickInventoryItem(0, id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillItemId, inventoryItems]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/hr/requisitions/mine" className="text-sm text-emerald-700 hover:underline">
          &larr; My Requisitions
        </Link>
      </div>

      <div className="rounded-2xl border-2 border-emerald-700 bg-white p-5 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Staff Requisition</p>
        <h1 className="mt-0.5 text-xl font-bold text-slate-900">New Requisition</h1>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Type selector */}
        <div className="rounded-2xl border bg-white p-5 shadow-sm space-y-3">
          <h2 className="border-b pb-2 font-bold text-slate-900">Requisition Type</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {TYPE_OPTS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => changeType(opt.value)}
                className={`rounded-2xl border-2 p-3 text-left transition-all ${type === opt.value ? "border-emerald-600 bg-emerald-50" : "border-slate-100 hover:border-emerald-200"}`}
              >
                <p className={`font-semibold text-sm ${type === opt.value ? "text-emerald-700" : "text-slate-800"}`}>{opt.label}</p>
                <p className="mt-0.5 text-xs text-slate-400">{opt.hint}</p>
              </button>
            ))}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">
              Department <span className="text-red-500">*</span>
            </label>
            {canPickAnyDepartment ? (
              <select
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : "")}
                required
                className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">— select department —</option>
                {(departments ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            ) : (
              // Locked to the requester's own department — only Procurement Officers (and
              // admins) can requisition on behalf of a different department.
              <div className="w-full rounded-xl border bg-slate-50 px-3 py-2 text-sm text-slate-600">
                {currentEmployee?.departmentName ?? "Loading your department…"}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">Purpose / Justification</label>
            <textarea
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              rows={2}
              placeholder="Briefly describe the need for this requisition…"
              className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        {/* Lines */}
        <div className="rounded-2xl border bg-white p-5 shadow-sm space-y-4">
          <h2 className="border-b pb-2 font-bold text-slate-900">
            {isGoods ? "Goods to Request from Store" : isAsset ? "Assets to Request" : "Requisition Lines"}
          </h2>

          {lines.map((line, i) => (
            <div key={i} className="grid gap-3 sm:grid-cols-4 items-end border-b pb-4 last:border-0 last:pb-0">

              {/* For GOODS: inventory item picker — required. The backend rejects (400) a GOODS
                  line with no linked inventoryItemId since there'd be nothing to check against
                  stock later, so this can't be left as free text. */}
              {isGoods && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">
                    Inventory Item <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={line.inventoryItemId ?? ""}
                    onChange={(e) => pickInventoryItem(i, Number(e.target.value))}
                    required
                    className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">— select item —</option>
                    {(inventoryItems ?? []).map(it => (
                      <option key={it.id} value={it.id}>{it.name} ({it.issueUnitName})</option>
                    ))}
                  </select>
                </div>
              )}

              {/* For ASSET_REQUEST: category picker — required, mirroring the GOODS/inventoryItemId
                  enforcement above. Not backend-enforced yet (flagged 2026-07-13 as the same
                  class of gap, just not closed server-side), but a category-less line is
                  equally unfulfillable, so guard it client-side regardless. */}
              {isAsset && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">
                    Asset Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={line.assetCategoryId ?? ""}
                    onChange={(e) => pickAssetCategory(i, Number(e.target.value))}
                    required
                    className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">— select category —</option>
                    {assignableCategories.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Description — always shown */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={line.description}
                  onChange={(e) => updateLine(i, "description", e.target.value)}
                  required
                  readOnly={isGoods || isAsset}
                  className={`w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${isGoods || isAsset ? "bg-slate-50 text-slate-500" : ""}`}
                  placeholder={isGoods ? "Select an inventory item above" : isAsset ? "Select an asset category above" : "Item or service description"}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-slate-400 mb-1">Qty</label>
                <input
                  type="number" min={1}
                  value={line.quantity}
                  onChange={(e) => updateLine(i, "quantity", Number(e.target.value))}
                  className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {lines.length > 1 && (
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeLine(i)}
                    className="rounded-xl border border-red-200 px-3 py-2 text-xs text-red-500 hover:bg-red-50"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ))}

          {isPlain && (
            <p className="text-xs text-slate-400">
              {type} requisitions don't have a fixed item classification — just describe what's needed.
            </p>
          )}

          <button
            type="button"
            onClick={addLine}
            className="rounded-xl border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
          >
            + Add Line
          </button>

        </div>

        <div className="flex justify-end gap-3">
          <Link to="/hr/requisitions/mine" className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-emerald-700 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Create Requisition"}
          </button>
        </div>
      </form>
    </div>
  );
}
